import React, { useEffect, useMemo, useState } from 'react';
import {
    X, Loader2, Phone, MessageSquare, Mail, FileText, StickyNote, Users,
    CalendarCheck, ClipboardList, Home, Send, Activity,
} from 'lucide-react';
import { getCrmAgentProfile } from '../../../api/kpi';
import {
    CLR, ENGAGEMENT, TEAL, formatValue, initials, scoreTier,
} from './crmAnalyticsUi';

/**
 * One agent's CRM report card.
 *
 * Opened by clicking any row on any CRM Analytics tab. It answers the question
 * a table cannot: what does this person's work actually look like? Their
 * headline in every section, then a time-stamped timeline of what they did.
 *
 * The timeline reads the same tables the tabs count, so anything on it can be
 * reconciled with a number on a tab — that is the point of it. A quiet timeline
 * is a real finding, not a loading failure, so an empty one says so plainly.
 */

const SECTION_TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'prospecting', label: 'Prospecting' },
    { id: 'database', label: 'Database' },
    { id: 'leads', label: 'Leads' },
    { id: 'buyers', label: 'Buyers' },
    { id: 'inspections', label: 'Inspections' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'appraisals', label: 'Appraisals' },
    { id: 'listings', label: 'Listings' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'timeline', label: 'Timeline' },
];

const KIND_ICON = {
    call: Phone,
    sms: MessageSquare,
    email: Mail,
    document_sent: FileText,
    note: StickyNote,
    prospect_note: StickyNote,
    checkin: Users,
    inspection: Home,
    task: ClipboardList,
    callback: CalendarCheck,
    lead: Activity,
    stage: Activity,
    esign: FileText,
    campaign: Send,
};

const dayLabel = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
};

const timeLabel = (iso) => new Date(iso).toLocaleTimeString('en-AU', {
    hour: 'numeric', minute: '2-digit',
});

const AgentProfileModal = ({ agentId, agentName, startDate, endDate, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState('overview');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        getCrmAgentProfile(agentId, { start_date: startDate, end_date: endDate })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((err) => {
                console.error('Failed to load agent profile:', err);
                if (!cancelled) {
                    setError(err?.response?.status === 403
                        ? 'You can only open your own profile.'
                        : 'Could not load this profile.');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [agentId, startDate, endDate]);

    const usage = data?.usage || {};
    const sections = data?.sections || {};
    const tier = scoreTier(usage.utilisation_score || 0);
    const engagement = ENGAGEMENT[usage.engagement_status] || null;

    // Group the timeline by day so a reader sees a working day at a time.
    const timelineDays = useMemo(() => {
        const groups = [];
        let current = null;
        for (const entry of data?.timeline || []) {
            const day = dayLabel(entry.at);
            if (!current || current.day !== day) {
                current = { day, entries: [] };
                groups.push(current);
            }
            current.entries.push(entry);
        }
        return groups;
    }, [data]);

    const visibleTabs = SECTION_TABS.filter(
        (t) => t.id === 'overview' || t.id === 'timeline' || sections[t.id]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b p-5" style={{ borderColor: CLR.border }}>
                    <div className="flex items-center gap-3">
                        <span
                            className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white"
                            style={{ background: TEAL }}
                        >
                            {initials(data?.agent?.agent_name || agentName)}
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {data?.agent?.agent_name || agentName}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {data?.agent?.agent_email}
                                {data?.agent?.agent_role ? ` • ${data.agent.agent_role}` : ''}
                            </p>
                        </div>
                        {usage.utilisation_score !== undefined ? (
                            <div className="ml-3 flex items-center gap-2">
                                <span
                                    className="rounded-lg px-2.5 py-1 text-sm font-bold"
                                    style={{ background: tier.bg, color: tier.fg }}
                                >
                                    {usage.utilisation_score}/100
                                </span>
                                {engagement ? (
                                    <span
                                        className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
                                        style={{ background: engagement.bg, color: engagement.fg }}
                                    >
                                        {engagement.label}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100" aria-label="Close">
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 overflow-x-auto border-b px-4 py-2" style={{ borderColor: CLR.border }}>
                    {visibleTabs.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                                tab === t.id ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            style={tab === t.id ? { background: TEAL } : undefined}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-20 text-gray-400">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : error ? (
                        <p className="py-16 text-center text-sm text-rose-600">{error}</p>
                    ) : tab === 'timeline' ? (
                        timelineDays.length === 0 ? (
                            <p className="py-16 text-center text-sm text-gray-500">
                                Nothing was logged in the CRM by this person in the selected period.
                            </p>
                        ) : (
                            <div className="space-y-5">
                                {timelineDays.map((group) => (
                                    <div key={group.day}>
                                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                                            {group.day}
                                        </p>
                                        <div className="space-y-1.5 border-l-2 pl-4" style={{ borderColor: CLR.border }}>
                                            {group.entries.map((entry, index) => {
                                                const Icon = KIND_ICON[entry.kind] || Activity;
                                                return (
                                                    <div key={`${entry.at}-${index}`} className="flex items-start gap-2.5">
                                                        <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                        <span className="w-16 flex-shrink-0 text-xs text-gray-400">
                                                            {timeLabel(entry.at)}
                                                        </span>
                                                        <span className="text-xs font-medium text-gray-800">
                                                            {entry.label}
                                                        </span>
                                                        {entry.detail ? (
                                                            <span className="truncate text-xs text-gray-500">
                                                                {entry.detail}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : tab === 'overview' ? (
                        <div className="space-y-5">
                            {sections.conversion?.funnel?.length ? (
                                <div>
                                    <h4 className="mb-2 text-sm font-bold text-gray-900">Their funnel</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {sections.conversion.funnel.map((stage) => (
                                            <div
                                                key={stage.key}
                                                className="min-w-[120px] flex-1 rounded-xl border bg-white p-3"
                                                style={{ borderColor: CLR.border }}
                                            >
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    {stage.label}
                                                </p>
                                                <p className="text-xl font-bold text-gray-900">
                                                    {formatValue(stage.value, 'int')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {Object.entries(sections).map(([slug, payload]) => (
                                payload.cards?.length ? (
                                    <div key={slug}>
                                        <h4 className="mb-2 text-sm font-bold capitalize text-gray-900">
                                            {slug}
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                                            {payload.cards.slice(0, 4).map((card) => (
                                                <div
                                                    key={card.key}
                                                    className="rounded-lg border bg-white p-3"
                                                    style={{ borderColor: CLR.border }}
                                                >
                                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                        {card.label}
                                                    </p>
                                                    <p className="text-lg font-bold text-gray-900">
                                                        {formatValue(card.value, card.format)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null
                            ))}
                        </div>
                    ) : sections[tab] ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                {sections[tab].cards.map((card) => (
                                    <div
                                        key={card.key}
                                        className="rounded-xl border bg-white p-3"
                                        style={{ borderColor: CLR.border }}
                                        title={card.hint || ''}
                                    >
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                            {card.label}
                                        </p>
                                        <p className="text-xl font-bold text-gray-900">
                                            {formatValue(card.value, card.format)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            {sections[tab].columns?.length ? (
                                <div className="overflow-hidden rounded-xl border" style={{ borderColor: CLR.border }}>
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {sections[tab].columns.map((column) => (
                                                <tr key={column.key} className="border-b last:border-0" style={{ borderColor: CLR.border }}>
                                                    <td className="px-4 py-2 text-xs text-gray-500">{column.label}</td>
                                                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                                        {formatValue(
                                                            sections[tab].row?.[column.key],
                                                            column.format === 'value' ? 'int' : column.format,
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <p className="py-16 text-center text-sm text-gray-500">Nothing to show.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgentProfileModal;
